import dayjs from "dayjs"
import { ref, toRef, toRefs, computed, defineComponent, watch, reactive, onMounted, inject, nextTick } from "vue"
import { useAuth } from "./useAuth"
import {
  setError,
  setSuccess,
  setWarning,
  clearNotifications,
  startLoading,
  stopLoading,
} from "../components/Notification"
import { useAttachments } from "../components/useAttachments"
import { confirmAsync } from "../components/Confirm"
import { getWorkOrderAsync, patchWorkOrderAsync, listServiceOrderDevicesAsync } from "../api"
import { EMPTY_GUID, DATETIME_FORMAT, DATETIME_FOR_LOCAL_INPUT, MAX_MINUTES_TIME_ENTRY } from "../utils/constants"

const useWorkOrder = (workOrderType, skipRoute = false, redirectIfNew = true) => {
  const route = VueRouter.useRoute()
  const router = VueRouter.useRouter()
  const { fetchAuthAsync } = useAuth()
  const { attachments, addAttachment, deleteAttachment } = useAttachments()

  const workOrder = ref(null)
  const devices = ref([])
  const hasUnsavedChanges = ref(false)

  const loadWorkOrderAsync = async (id) => {
    try {
      const temp = await getWorkOrderAsync(fetchAuthAsync, workOrderType, id)
      if (!!temp) {
        temp.isNew = id === "new"
      }
      if (!!temp && workOrderType === "Service") {
        devices.value = await listServiceOrderDevicesAsync(fetchAuthAsync, id)
        Object.defineProperty(temp, "datePlannedVisitObject", {
          get: function () {
            const d = dayjs.utc(this.datePlannedVisit)
            return !d.isValid() || d.year() < 2000 ? undefined : d
          },
        })
        Object.defineProperty(temp, "datePlannedVisitForInput", {
          get: function () {
            return this.datePlannedVisitObject?.local()?.format("YYYY-MM-DD")
          },
          set: function (v) {
            this.datePlannedVisit = v ? dayjs(v).format("YYYY-MM-DD") : v
          },
        })
      }
      workOrder.value = temp
      attachments.value = temp?.attachments
      // Wait for watcher to fire, then reset flag
      await nextTick()
      hasUnsavedChanges.value = false
    } catch (e) {
      console.error("loadWorkOrderAsync error", e)
    }
  }

  const sendEmailAsync = async (emailType, options = {}) => {
    const loadingKey = `sendEmailAsync_${emailType}`
    startLoading(loadingKey)
    try {
      const payload = {
        Type: "SendServiceOrderEmail",
        ServiceOrderId: workOrder.value.id,
        EmailType: emailType,
      }

      let successMessage = ""
      let shouldNavigate = false

      switch (emailType) {
        case "plannedVisit":
          // #1761: an SDN in a route has no date of its own; the API uses the route's day
          payload.DatePlannedVisit = workOrder.value.datePlannedVisit || workOrder.value.plannedRoute?.day
          successMessage = "Termin poslan."
          break

        case "resendCompleted":
          const facility = options.facility || workOrder.value.facility
          payload.SendToFacilityContacts = facility?.contacts?.filter((c) => !!c.checked).map((c) => c.id)
          if (!payload.SendToFacilityContacts || payload.SendToFacilityContacts?.length === 0) {
            throw new Error("Izberite vsaj en kontakt za pošiljanje emaila.")
          }
          successMessage = "Poslano znova"
          shouldNavigate = true
          break

        case "hotWorkPermit":
          payload.RecipientType = options.recipientType
          successMessage = "Dokument požarne straže poslan."
          break

        default:
          throw new Error(`Unknown email type: ${emailType}`)
      }

      const raw = await fetchAuthAsync(`${AZURE_URI}/ServiceOrder/Command`, "POST", payload)
      if (raw?.status !== "ok") {
        throw raw
      }

      setSuccess({ message: successMessage })
      if (shouldNavigate) {
        router.push({ name: `Service` })
      } else {
        await loadWorkOrderAsync(workOrder.value.id)
      }
    } catch (e) {
      setError(e)
    }
    stopLoading(loadingKey)
  }

  const changeWorkOrderStatusAsync = async (status) => {
    if (typeof status !== "string") {
      setError("status must be a string")
      return false
    }
    const result = await patchWorkOrderAsync(fetchAuthAsync, { status }, workOrderType, workOrder.value.id)
    if (result === true) {
      setSuccess({ message: "Status spremenjen" })
      await loadWorkOrderAsync(workOrder.value.id)
    }
    return result
  }

  const verifyStockOrderAsync = async () => {
    const saveResult = await saveWorkOrderAsync()
    const result = await patchWorkOrderAsync(
      fetchAuthAsync,
      { status: "OrderMaterial" },
      workOrderType,
      workOrder.value.id,
    )
    if (result === true && saveResult === true) {
      setSuccess({ message: "Shranjeno in v naročanje materiala" })
      await loadWorkOrderAsync(workOrder.value.id)
    }
  }

  const clearCommissionsAsync = async () => {
    const result = await patchWorkOrderAsync(
      fetchAuthAsync,
      { clearCommissions: true },
      workOrderType,
      workOrder.value.id,
    )
    if (result === true) {
      setSuccess({ message: "Komisioni resetirani." })
      await loadWorkOrderAsync(workOrder.value.id)
    }
  }

  const runOfferAsync = async (isAction = false, data = {}) => {
    startLoading("runOfferAsync")
    try {
      // console.log("runOfferAsync (sdn, action, data)", workOrder.value.id, isAction, data)
      clearNotifications()
      const raw = await fetchAuthAsync(`${AZURE_URI}/Offer/${workOrder.value.id}`, isAction ? "PATCH" : "POST", data)
      if (raw?.status !== "ok") {
        throw raw
      }
      stopLoading("runOfferAsync")
      return true
    } catch (e) {
      setError(e)
    }
    stopLoading("runOfferAsync")
    return false
  }

  const deleteOrderAsync = async () => {
    if (
      !(await confirmAsync({
        message: "Ali ste prepričani, da želite izbrisati ta delovni nalog?",
        confirmText: "Izbriši",
        variant: "danger",
      }))
    ) {
      return
    }
    const result = await patchWorkOrderAsync(fetchAuthAsync, { status: "Deleted" }, workOrderType, workOrder.value.id)
    if (result === true) {
      setSuccess({ message: "Izbrisano" })
      await loadWorkOrderAsync(workOrder.value.id)
    }
  }

  const markCompletedAsync = async (skipSuccessRoute = false) => {
    if (typeof skipSuccessRoute !== "boolean") {
      console.warn("markCompletedAsync parameter incorrect type", skipSuccessRoute)
    }
    const result = await patchWorkOrderAsync(fetchAuthAsync, { status: "Completed" }, workOrderType, workOrder.value.id)
    if (result === true) {
      setSuccess({ message: 'Označeno za "Opravljeno".' })
      if (!skipSuccessRoute) {
        console.log("push")
        router.push({ name: workOrderType })
      }
    }
    return result
  }

  const changeOwnerAsync = async (workOrderId, newOwnerId) => {
    if (newOwnerId?.length !== 36) {
      setError("missing newOwnerId" + newOwnerId)
      return false
    }
    const result = await patchWorkOrderAsync(fetchAuthAsync, { newOwnerId }, workOrderType, workOrderId)
    if (result === true) {
      setSuccess({ message: "Spremenjen lastnik." })
    }
    return result
  }

  const saveLocationAsync = async (location) => {
    if (location && (location.lat > 0 || location.lat < 0)) {
      const raw2 = await fetchAuthAsync(`${AZURE_URI}/Location`, "POST", location)
      if (raw2?.status !== "ok") {
        console.error(raw2)
      }
    }
  }

  const saveWorkOrderAsync = async (location = null) => {
    let result = false
    startLoading("saveWorkOrderAsync")
    try {
      clearNotifications()
      if (workOrder.value.dueDate === "Invalid Date") {
        workOrder.value.dueDate = ""
      }

      if (!workOrder.value.isNew && workOrderType === "Service") {
        const payload = devices.value.map((d) => {
          return { deviceId: d.id, ...d }
        })
        const raw1 = await fetchAuthAsync(`${AZURE_URI}/ServiceOrder/Devices/` + workOrder.value.id, "PUT", payload)
        if (raw1?.status !== "ok" || raw1?.result !== true) {
          throw raw1
        }
      }

      const raw = await fetchAuthAsync(
        `${AZURE_URI}/${workOrderType}Order`,
        workOrder.value.isNew ? "POST" : "PUT",
        workOrder.value,
      )
      if (raw?.status !== "ok") {
        throw raw
      }

      // a new order needs to exist before its devices can be attached (e.g. "Nov SDN" on a device, #1748)
      if (workOrder.value.isNew && workOrderType === "Service" && devices.value.some((d) => d.isRelated)) {
        const payload = devices.value.map((d) => {
          return { deviceId: d.id, ...d }
        })
        const raw1 = await fetchAuthAsync(`${AZURE_URI}/ServiceOrder/Devices/` + workOrder.value.id, "PUT", payload)
        if (raw1?.status !== "ok" || raw1?.result !== true) {
          throw raw1
        }
      }

      await saveLocationAsync(location)

      setSuccess({ message: "Shranjeno" })
      if (workOrder.value.isNew && redirectIfNew) {
        router.push({ name: `${workOrderType}Order`, params: { workOrderId: workOrder.value.id } })
      }
      result = true
      hasUnsavedChanges.value = false
      await loadWorkOrderAsync(workOrder.value.id)
    } catch (e) {
      setError(e)
    }
    stopLoading("saveWorkOrderAsync")
    return result
  }

  const saveAsNewWorkOrderAsync = async (location = null) => {
    let result = false
    startLoading("saveAsNewWorkOrderAsync")
    try {
      clearNotifications()
      if (workOrder.value.dueDate === "Invalid Date") {
        workOrder.value.dueDate = ""
      }

      const raw = await fetchAuthAsync(`${AZURE_URI}/${workOrderType}OrderAsNew`, "POST", workOrder.value)
      if (raw?.status !== "ok" || !raw?.id) {
        throw raw
      }
      const workOrderId = raw.id
      await saveLocationAsync(location)

      setSuccess({ message: "Shranjeno kot nov" })
      router.push({ name: `${workOrderType}Order`, params: { workOrderId } })
      result = true
      await loadWorkOrderAsync(workOrderId)
    } catch (e) {
      setError(e)
    }
    stopLoading("saveAsNewWorkOrderAsync")
    return result
  }

  watch(
    workOrder,
    () => {
      hasUnsavedChanges.value = true
    },
    { deep: true },
  )

  if (!skipRoute) {
    watch(
      () => route.params.workOrderId,
      async (newId) => {
        if (newId) {
          await loadWorkOrderAsync(newId)
          hasUnsavedChanges.value = false
          if (newId === "new" && route.params.customerID && route.params.facilityID) {
            if (!workOrder.value) {
              workOrder.value = {}
            }
            workOrder.value.facilityID = route.params.facilityID
            workOrder.value.customerID = route.params.customerID
            console.log("new with customer and facility", newId)
          }
          if (newId === "new" && route.params.projectID && route.params.projectID !== EMPTY_GUID) {
            workOrder.value.projectID = route.params.projectID
            workOrder.value.isSpecial = true
            console.log("new with project", newId)
          }
        }
      },
      { immediate: true },
    )
  }

  return {
    workOrder,
    hasUnsavedChanges,
    sendEmailAsync,
    loadWorkOrderAsync,
    saveWorkOrderAsync,
    deleteOrderAsync,
    markCompletedAsync,
    changeOwnerAsync,
    attachments,
    devices,
    addAttachment,
    verifyStockOrderAsync,
    saveAsNewWorkOrderAsync,
    clearCommissionsAsync,
    changeWorkOrderStatusAsync,
    runOfferAsync,
    deleteAttachment,
  }
}

export { useWorkOrder }
